import { useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import {
  convertToCurrencyFormat,
  stockUnitOfMeasureOptions,
  isReadOnlyUser,
  isManagementUser,
  getSerialFromBarcode,
} from "../../../../shared/utility";
import SelectMenu from "../../../controls/SelectMenu";

export default function OrderSubtable1(props) {
  const { data, onItemEditComplete } = props;
  const [editingUnitTypes, setEditingUnitTypes] = useState({});

  const getEditingUnitType = (rowData) => {
    if (editingUnitTypes[rowData.id] !== undefined) {
      return editingUnitTypes[rowData.id];
    }
    return parseInt(rowData.selling_unit_of_measure, 10);
  };

  const clearEditingUnitType = (rowId) => {
    setEditingUnitTypes((prev) => {
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
  };

  const bodyProductName = (item) => {
    const productName = item.manufactured_product?.product?.name || "";
    const plu = item.manufactured_product?.plu || "";
    return (
      <div className="flex gap-2">
        {item.is_legacy === 1 ? <p className="text-lg my-auto">L</p> : null}
        <div>
          {productName} <br /> <span className="font-bold">PLU: {plu}</span>
        </div>
      </div>
    );
  };

  const rowStyleClass = (data) => {
    if (data.credit_return_id > 0) {
      return { "bg-cyan-100": true };
    } else if (data.is_returned === 0) {
      return { "bg-gray-100": true };
    } else if (data.is_returned === 1) {
      return { "bg-yellow-100": true };
    }
    return {};
  };

  const barcodeBody = (rowData) => {
    const returned =
      rowData.credit_return_id > 0
        ? "CR"
        : rowData.is_returned === 1
          ? " RETURNED"
          : null;

    return (
      <p>
        {getSerialFromBarcode(rowData.manufactured_product.barcode)} {returned}
      </p>
    );
  };

  const weightInKgBody = (rowData) => {
    return (
      <div>
        <p>{Number(rowData.net_weight_kg ?? 0).toFixed(2)} kg</p>
        <p>{Number(rowData.net_weight_lbs ?? 0).toFixed(2)} lbs</p>
      </div>
    );
  };

  const pieceCountBody = (rowData) => {
    return <p>{Number(rowData.piece_count ?? 0).toFixed(0)} pieces</p>;
  };

  const unitCostBody = (rowData) => {
    return <p>{convertToCurrencyFormat(rowData.unit_cost)}</p>;
  };

  const totalCostBody = (rowData) => {
    return <p>{convertToCurrencyFormat(rowData.total_cost)}</p>;
  };

  const unitTypeBody = (rowData) => {
    const unitType = rowData.selling_unit_of_measure;
    switch (unitType) {
      case 1:
        return "kg";
      case 2:
        return "lbs";
      case 3:
        return "pieces";
      default:
        return rowData.selling_unit_of_measure_name || "N/A";
    }
  };

  const textEditor = (options) => {
    return (
      <InputText
        type="text"
        style={{ minWidth: "8rem" }}
        className="h-9"
        value={options.value}
        onChange={(e) => options.editorCallback(e.target.value)}
        onKeyDown={(e) => e.stopPropagation()}
      />
    );
  };

  const weightEditor = (options) => {
    const unitType = getEditingUnitType(options.rowData);

    if (unitType === 2) {
      return (
        <div>
          <p>{Number(options.rowData.net_weight_kg ?? 0).toFixed(2)} kg</p>
          <InputNumber
            value={options.rowData.net_weight_lbs}
            onValueChange={(e) => {
              options.rowData.net_weight_lbs = e.value;
              options.editorCallback(options.rowData.net_weight_kg);
            }}
            minFractionDigits={2}
            maxFractionDigits={2}
            onKeyDown={(e) => e.stopPropagation()}
          />
        </div>
      );
    }

    return (
      <div>
        <InputNumber
          value={options.rowData.net_weight_kg}
          onValueChange={(e) => {
            options.rowData.net_weight_kg = e.value;
            options.editorCallback(e.value);
          }}
          minFractionDigits={2}
          maxFractionDigits={2}
          onKeyDown={(e) => e.stopPropagation()}
        />
        <p>{Number(options.rowData.net_weight_lbs ?? 0).toFixed(2)} lbs</p>
      </div>
    );
  };

  const pieceCountEditor = (options) => {
    if (getEditingUnitType(options.rowData) !== 3) {
      return pieceCountBody(options.rowData);
    }

    return (
      <InputNumber
        value={options.rowData.piece_count}
        onValueChange={(e) => options.editorCallback(e.value)}
        minFractionDigits={0}
        maxFractionDigits={0}
        onKeyDown={(e) => e.stopPropagation()}
      />
    );
  };

  const itemTypeDropdown = (options) => {
    const unitType = getEditingUnitType(options.rowData);
    return (
      <SelectMenu
        handleInputChange={(e) => {
          const newUnitType = e.target.value.value;
          options.rowData.selling_unit_of_measure = newUnitType;
          options.editorCallback(newUnitType);
          setEditingUnitTypes((prev) => ({
            ...prev,
            [options.rowData.id]: newUnitType,
          }));
        }}
        name="selling_unit_of_measure"
        options={stockUnitOfMeasureOptions}
        value={stockUnitOfMeasureOptions.find((el) => el.value === unitType)}
        showLabel={false}
        menuPortalTarget={document.body}
      />
    );
  };

  const onRowEditComplete = (e) => {
    let { newData, index } = e;
    clearEditingUnitType(newData.id);

    if (!onItemEditComplete) return;

    // Ensure unit_cost is properly formatted as string with 4 decimals
    const unitCost = parseFloat(newData.unit_cost || 0);
    newData["unit_cost"] = unitCost.toFixed(4);
    newData["net_weight_kg"] = parseFloat(newData.net_weight_kg || 0).toFixed(
      2,
    );
    newData["net_weight_lbs"] = parseFloat(newData.net_weight_lbs || 0).toFixed(
      2,
    );
    newData["piece_count"] = parseInt(newData.piece_count || 0, 10);

    var newTotalCost = 0;
    const unitType = parseInt(newData.selling_unit_of_measure, 10);

    if (unitType === 2) {
      // lbs
      newTotalCost = unitCost * parseFloat(newData.net_weight_lbs || 0);
    } else if (unitType === 3) {
      // pieces
      newTotalCost = unitCost * parseFloat(newData.piece_count || 0);
    } else {
      // kg (default)
      newTotalCost = unitCost * parseFloat(newData.net_weight_kg || 0);
    }

    // Update both total_cost and ensure selling_unit_of_measure is set
    newData["total_cost"] = newTotalCost.toFixed(4);
    newData["selling_unit_of_measure"] = unitType;
    newData["selling_unit_of_measure_name"] =
      stockUnitOfMeasureOptions.find((el) => el.value === unitType)?.label ||
      "";
    newData["subtotal_amount"] = newTotalCost.toFixed(4);
    newData["tax_amount"] = "0.0000";

    onItemEditComplete(newData, index);
  };

  const isReadOnly = isReadOnlyUser();
  const isManagement = isManagementUser();

  return (
    <div>
      <DataTable
        value={data}
        dataKey="id"
        tableClassName="min-w-full bg-white divide-y divide-gray-300 text-sm custom-datatable-items-table shipping-sub-items-table"
        cellClassName="py-2 border-1-bottom-gray px-2 text-sm"
        rowClassName={rowStyleClass}
        editMode="row"
        onRowEditInit={(e) => {
          setEditingUnitTypes((prev) => ({
            ...prev,
            [e.data.id]: parseInt(e.data.selling_unit_of_measure, 10),
          }));
        }}
        onRowEditCancel={(e) => clearEditingUnitType(e.data.id)}
        onRowEditComplete={onRowEditComplete}
      >
        <Column
          header="Product"
          body={bodyProductName}
          style={{ width: "15%" }}
        />
        <Column
          header="Lot Number"
          field="manufactured_product.lot_number"
          style={{ width: "12%" }}
        />
        <Column header="Serial" body={barcodeBody} style={{ width: "12%" }} />
        <Column
          header="Weight (kg)"
          field="net_weight_kg"
          body={weightInKgBody}
          editor={(options) => weightEditor(options)}
          style={{ width: "10%", textAlign: "right" }}
        />
        <Column
          header="Piece Count"
          field="piece_count"
          body={pieceCountBody}
          editor={(options) => pieceCountEditor(options)}
          style={{ width: "10%", textAlign: "right" }}
        />
        {isManagement ? (
          <Column
            header="Unit Price"
            field="unit_cost"
            style={{ width: "10%", textAlign: "right" }}
            body={unitCostBody}
            editor={(options) => textEditor(options)}
          />
        ) : null}
        {isManagement ? (
          <Column
            header="Subtotal Price"
            field="subtotal_amount"
            style={{ width: "10%", textAlign: "right" }}
            body={totalCostBody}
          />
        ) : null}
        <Column
          header="Unit Type"
          field="selling_unit_of_measure"
          headerStyle={{ minWidth: "8rem" }}
          body={unitTypeBody}
          editor={(options) => itemTypeDropdown(options)}
        />
        {!isReadOnly && (
          <Column
            rowEditor={true}
            headerStyle={{ minWidth: "6rem" }}
            bodyStyle={{ textAlign: "center" }}
          />
        )}
      </DataTable>
    </div>
  );
}
